import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  UsersIcon, 
  ChartBarIcon, 
  CogIcon, 
  ArrowLeftOnRectangleIcon,
  ListBulletIcon,
  BellIcon,
  UserCircleIcon,
  AcademicCapIcon,
  FolderIcon,
  RocketLaunchIcon,
  DocumentIcon,
  SunIcon,
  MoonIcon,
  ClipboardDocumentListIcon,
  RectangleGroupIcon,
  DocumentTextIcon,
  XMarkIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleResize = useCallback(() => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setIsSidebarOpen(false);
    }
  }, []);

  useEffect(() => {
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    navigate('/login');
  };

  const navItems = [
    { 
      icon: ChartBarIcon, 
      label: 'Dashboard', 
      path: '/dashboard' 
    },
    { 
      icon: ListBulletIcon, 
      label: 'Categories', 
      path: '/categories' 
    },
    { 
      icon: FolderIcon, 
      label: 'Sub-Categories', 
      path: '/sub-categories' 
    },
    { 
      icon: AcademicCapIcon, 
      label: 'Levels', 
      path: '/levels' 
    },
    { 
      icon: RocketLaunchIcon, 
      label: 'Goals', 
      path: '/goals' 
    },
    { 
      icon: RectangleGroupIcon, 
      label: 'Trainings', 
      path: '/trainings' 
    },
    { 
      icon: DocumentIcon, 
      label: 'Exercises', 
      path: '/exercises' 
    },
    { 
      icon: UsersIcon, 
      label: 'Users', 
      path: '/users' 
    },
    { 
      icon: ClipboardDocumentListIcon, 
      label: 'Meal Plans', 
      path: '/meal-plans' 
    },
    { 
      icon: DocumentTextIcon, 
      label: "Doctor's Advice", 
      path: '/doctor-advice' 
    },
    { 
      icon: CreditCardIcon, 
      label: 'Subscription Packages', 
      path: '/subscription-packages' 
    }
  ];

  const sidebarVariants = {
    hidden: { 
      x: '-100%', 
      transition: { 
        type: 'tween',
        duration: 0.3
      }
    },
    visible: { 
      x: 0, 
      transition: { 
        type: 'tween',
        duration: 0.3
      }
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black z-40 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <motion.aside 
        variants={sidebarVariants}
        initial={false}
        animate={isMobile ? (isSidebarOpen ? "visible" : "hidden") : "visible"}
        className={`
          fixed lg:static top-0 left-0 w-64 h-full 
          bg-white dark:bg-gray-800 shadow-lg z-50 flex flex-col
          transform transition-transform duration-300
          ${isMobile 
            ? 'lg:translate-x-0' 
            : 'translate-x-0'
          }
        `}
      >
        {/* Logo and Brand */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">Fitness</span>
            <span className="text-2xl font-light ml-1 dark:text-gray-300">Admin</span>
          </div>
          {isMobile && (
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsSidebarOpen(false)}
              className="text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-2"
            >
              <XMarkIcon className="h-6 w-6" />
            </motion.button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-grow py-4 overflow-y-auto">
          <ul className="space-y-1 px-4">
            {navItems.map((item) => (
              <motion.li 
                key={item.path}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to={item.path}
                  className={`
                    flex items-center px-4 py-2.5 rounded-lg transition-all duration-200
                    ${location.pathname === item.path 
                      ? 'bg-blue-50 dark:bg-blue-900 text-blue-600 dark:text-blue-400 font-semibold' 
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100'}
                  `}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.label}
                </Link>
              </motion.li>
            ))}
          </ul>
        </nav>

        {/* Theme Toggle and Logout */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
          {/* Theme Toggle */}
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            className="w-full flex items-center justify-center py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-300"
          >
            {isDarkMode ? (
              <>
                <SunIcon className="h-5 w-5 mr-2" />
                Light Mode
              </>
            ) : (
              <>
                <MoonIcon className="h-5 w-5 mr-2" />
                Dark Mode
              </>
            )}
          </motion.button>

          {/* Logout */}
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            className="w-full flex items-center justify-center py-2.5 bg-red-500 dark:bg-red-700 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-800 transition-colors duration-300"
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-2" />
            Logout
          </motion.button>
        </div>
      </motion.aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-full mx-auto px-6 py-4 flex justify-between items-center">
            {/* Mobile Menu Toggle */}
            {isMobile && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsSidebarOpen(true)}
                className="mr-4 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-2"
              >
                <RectangleGroupIcon className="h-6 w-6" />
              </motion.button>
            )}

            {/* Page Title */}
            <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>

            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="relative p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <BellIcon className="h-6 w-6" />
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
              </motion.button>

              {/* User Profile */}
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-all"
              >
                <UserCircleIcon className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Admin User</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Administrator</p>
                </div>
              </motion.div>

              {/* Settings */}
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => navigate('/settings')}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <CogIcon className="h-6 w-6" />
              </motion.button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
